<template>
  <b-modal ref="task-assignees-modal"
           modal-class="task-modal v2-modal"
           size="lg"
           @hide="error = ''">
    <template #modal-header>
      <div class="v2-modal__head">
        <div class="v2-modal__titles">
          <div class="v2-modal__eyebrow">{{ project.title }} · {{ $tc('projects.engagement.assignees.task_count', taskCount, { count: taskCount }) }}</div>
          <h3 data-qid="task-assignees-title">{{ $t('projects.engagement.assignees.title') }}</h3>
        </div>
        <span class="task-panel__nav" @click="hide" data-qid="task-assignees-close"><font-awesome-icon icon="times"/></span>
      </div>
    </template>

    <b-alert v-if="error.length"
             show
             variant="danger"
             data-qid="task-assignees-error">
      {{ error }}
    </b-alert>

    <div v-if="loading" class="text-center text-muted">
      <font-awesome-icon icon="spinner" class="fa-spin"/>
    </div>

    <div v-else class="form-container">
      <!-- Add or remove users on every task; other assignments are left as they are -->
      <div data-qid="task-assignees-bulk">
        <p class="v2-assign__intro">{{ $t('projects.engagement.assignees.bulk_description') }}</p>

        <b-form-group>
          <label>{{ $t('projects.engagement.assignees.bulk_action_label') }}</label>
          <div class="task-choices" data-qid="task-assignees-bulk-action">
            <span v-for="option in bulkActionOptions"
                  :key="'action-' + option.value"
                  class="task-choice task-choice--teal"
                  :class="{'task-choice--on': bulk.action === option.value}"
                  @click="bulk.action = option.value; bulk.users = []"
            >
              <font-awesome-icon :icon="option.value === 'add' ? 'plus' : 'minus'"/>{{ option.text }}
            </span>
          </div>
        </b-form-group>

        <b-form-group>
          <label>{{ $t('projects.engagement.assignees.bulk_tasks_label') }}</label>
          <div class="task-choices" data-qid="task-assignees-bulk-kind">
            <span v-for="option in bulkKindOptions"
                  :key="'kind-' + option.value"
                  class="task-choice task-choice--teal"
                  :class="{'task-choice--on': bulk.kind === option.value}"
                  @click="bulk.kind = option.value; bulk.users = []"
            >
              <font-awesome-icon :icon="option.value === 'main' ? 'user-tag' : 'folder-open'"/>{{ option.text }}
            </span>
          </div>
        </b-form-group>

        <b-form-group>
          <label>{{ $t('projects.engagement.assignees.bulk_users_label') }}</label>
          <div class="multiselect-row">
            <multiselect
                v-model="bulk.users"
                :options="bulkUserOptions"
                :multiple="true"
                :taggable="false"
                :internal-search="!bulkSearchesServer"
                :disabled="buttonDisabled"
                track-by="id"
                label="name"
                @search-change="bulkSearch"
                :select-label="$t('system.dropdown_select_label')"
                :selected-label="$t('system.dropdown_selected_label')"
                :deselect-label="$t('system.dropdown_deselect_label')"
                :placeholder="$t('projects.engagement.assignees.bulk_users_label')"
                data-qid="task-assignees-bulk-users"
            >
              <template slot="tag" slot-scope="props">
                <span class="mr-3 user-tag">
                  <Avatar :object="props.option" size="xs"></Avatar>
                  <span class="ml-2">{{ props.option.name }}</span>
                </span>
              </template>
              <template slot="option" slot-scope="props">
                <UserDropdownOption :user="props.option"></UserDropdownOption>
              </template>
              <template slot="noOptions">{{ $t('system.no_results') }}</template>
            </multiselect>

            <UserInviteModal v-if="bulkSearchesServer"
                             :buttonType="'small'"
                             :buttonDisabled="buttonDisabled"
                             @reloadUserList="loadUsers()"
            ></UserInviteModal>
          </div>
          <small class="form-text text-muted" v-if="bulk.action === 'add' && bulk.kind === 'main'">
            {{ $t('projects.engagement.assignees.main_hint') }}
          </small>
        </b-form-group>

        <div class="task-assignees-changes" v-if="bulk.users.length" data-qid="task-assignees-bulk-summary">
          <div v-for="user in bulk.users" :key="'bulk-' + user.id" class="task-assignees-changes__item">
            {{ bulkSummary(user) }}
          </div>
          <small class="form-text text-muted">{{ $t('projects.engagement.assignees.untouched_hint') }}</small>
        </div>
      </div>

    </div>

    <template #modal-footer>
      <div class="v2-modal__footer">
        <b-button variant="secondary" @click="hide" data-qid="task-assignees-cancel">
          {{ $t('projects.create.cancel') }}
        </b-button>
        <b-button variant="success"
                  :disabled="buttonDisabled || loading || !bulk.users.length"
                  @click="saveBulk"
                  data-qid="task-assignees-save"
        >
          <font-awesome-icon v-if="buttonDisabled"
                             icon="spinner"
                             size="sm"
                             class="fa-spin mr-1"
          />
          {{ $t('projects.engagement.assignees.bulk_save_' + bulk.action) }}
        </b-button>
      </div>
    </template>
  </b-modal>
</template>

<script>
import piincHttp from '@/util/configureAxios.js';
import Avatar from '@/components/Avatar.vue';
import UserDropdownOption from '@/components/UserDropdownOption.vue';
import UserInviteModal from '@/components/user/UserInviteModal.vue';

export default {
  name: 'TaskAssigneesModal',
  props: {
    project: {
      type: Object,
      required: true
    }
  },
  components: {
    UserDropdownOption,
    Avatar,
    UserInviteModal
  },
  data: function() {
    return {
      loading: false,
      buttonDisabled: false,
      error: '',
      users: [],
      mainCounts: {},
      dataCollectionCounts: {},
      taskCount: 0,
      mainAssignees: [],
      dataCollectionAssignees: [],
      bulk: {
        action: 'add',
        kind: 'main',
        users: []
      },
      bulkActionOptions: [
        { text: this.$t('projects.engagement.assignees.bulk_add'), value: 'add' },
        { text: this.$t('projects.engagement.assignees.bulk_remove'), value: 'remove' }
      ],
      bulkKindOptions: [
        { text: this.$t('projects.engagement.assignees.main_title'), value: 'main' },
        { text: this.$t('projects.engagement.assignees.data_collection_title'), value: 'data_collection' }
      ]
    }
  },
  computed: {
    getClient() {
      return this.$store.state.client
    },
    teamUsers() {
      // Only engagement team members can be assigned to main tasks
      return this.project.users || [];
    },
    // Adding to documentation collection tasks can pick any user of the client, so the list is searched on the server
    bulkSearchesServer() {
      return this.bulk.action === 'add' && this.bulk.kind === 'data_collection';
    },
    bulkUserOptions() {
      if (this.bulk.action === 'remove') {
        return this.bulk.kind === 'main' ? this.mainAssignees : this.dataCollectionAssignees;
      }
      if (this.bulk.kind === 'main') {
        return this.teamUsers;
      }
      return _.uniqBy(this.bulk.users.concat(this.users), 'id');
    }
  },
  methods: {
    show() {
      let self = this;
      this.error = '';
      this.loading = true;
      this.bulk = { action: 'add', kind: 'main', users: [] };
      this.loadUsers();
      this.$refs['task-assignees-modal'].show();

      piincHttp.get('projects/' + this.project.slug + '/task-assignees').then(function(response) {
        self.taskCount = response.data.task_count;
        self.mainCounts = response.data.main_counts || {};
        self.dataCollectionCounts = response.data.data_collection_counts || {};
        self.mainAssignees = response.data.main_assignees || [];
        self.dataCollectionAssignees = response.data.data_collection_assignees || [];
        self.loading = false;
      }, function(error) {
        self.loading = false;
        self.error = error.data && error.data.message ? error.data.message : '';
      });
    },
    hide() {
      this.$refs['task-assignees-modal'].hide();
    },
    loadUsers(query) {
      let self = this;
      piincHttp.get('users', { params:
        {
          client: this.getClient.id,
          keyword: query ? query : undefined,
          per_page: 100
        }
      }).then(function(response) {
        self.users = response.data.items;
      }, function() {});
    },
    bulkSearch(query) {
      if (this.bulkSearchesServer) {
        this.loadUsers(query);
      }
    },
    bulkSummary(user) {
      const counts = this.bulk.kind === 'main' ? this.mainCounts : this.dataCollectionCounts;
      const assigned = counts[user.id] || 0;
      if (this.bulk.action === 'add') {
        return this.$t('projects.engagement.assignees.add_change_' + this.bulk.kind, { name: user.name, count: this.taskCount - assigned, total: this.taskCount });
      }
      return this.$t('projects.engagement.assignees.remove_change_' + this.bulk.kind, { name: user.name, count: assigned });
    },
    saveBulk() {
      let self = this;
      this.buttonDisabled = true;
      this.error = '';

      // Only the chosen users change; everyone else's assignments stay as they are
      let payload = {};
      payload[this.bulk.action + '_' + this.bulk.kind] = _.map(this.bulk.users, 'id');

      piincHttp.put('projects/' + this.project.slug + '/task-assignees', payload).then(function() {
        self.buttonDisabled = false;
        self.hide();
        self.$toastr('success', self.$t('projects.engagement.assignees.success'));
        self.$emit('updated');
      }, function(error) {
        self.buttonDisabled = false;
        self.error = error.data && error.data.message ? error.data.message : '';
      });
    }
  }
}
</script>
